import ExpirationDateField from "./ExpirationDateField";
import { expirationDatePayload } from "../../utils/expirationDate";
import { currentReceivingPermissions, useReceivingPermissions } from "../../utils/receivingPermissions";
import { readStoredUser } from "../../../utils/sessionUser";
import { isActivePurchaseSupplier } from "../../utils/purchaseSuppliers";
import { itemMatchesSupplier, itemSupplierMatches, supplierIdentityIds } from "../../utils/supplierIdentity";
import { useAuthorizedReceivingDestinations, useDestinationSaveQueue, ReceivingLocationPicker, permittedReceivingLocations, receivingLineLocation } from "./ReceivingDestinations";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import axios from "../../../api/axiosInstance";
import useIsMobile from "../../../hooks/useIsMobile";
import AddItemPopup from "./AddItemPopup";
import PendingReceivingOrders from "./PendingReceivingOrders";
import { receivingPopupStatus, useReceivingPopupUi, receivingErrorMessage } from "./receivingPopupMessages";
import "../../PurchasingUi.css";
import "./PurchasingReceivingPopups.css";

const numberInputStyle = `
  .purchasing-receiving-ui input[type=number]::-webkit-inner-spin-button,
  .purchasing-receiving-ui input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .purchasing-receiving-ui input[type=number] {
    -moz-appearance: textfield;
  }
`;

const popupCss = `
  .arp-wrap{position:fixed;inset:0;z-index:9999;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:16px}
  .arp-modal{width:min(1480px,97vw);max-height:calc(100vh - 24px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);color:var(--pu-text);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .arp-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;padding:22px 24px 14px;border-bottom:1px solid var(--pu-border)}
  .arp-headMain{flex:1;min-width:0}
  .arp-headActions{display:flex;align-items:center;gap:10px;flex-shrink:0}
  .arp-title{margin:0;font-size:clamp(1.25rem,2vw,1.72rem);font-weight:900;line-height:1.05}
  .arp-sub{margin-top:8px;font-size:.9rem;color:var(--pu-muted)}
  .arp-badge{padding:6px 11px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.72rem;font-weight:800;letter-spacing:.08em}
  .arp-closeBtn{width:40px;min-width:40px;height:40px;border-radius:14px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.92rem;font-weight:800;display:inline-flex;align-items:center;justify-content:center;padding:0;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
  .arp-closeBtn:hover{transform:translateY(-1px);background:var(--pu-soft);border-color:var(--pu-border);box-shadow:none}
  .arp-body{padding:16px 24px 24px}
  .arp-section{border:1px solid var(--pu-border);border-radius:20px;padding:18px;background:var(--pu-soft);box-shadow:none}
  .arp-section + .arp-section{margin-top:16px}
  .arp-sectionTitle{margin:0 0 4px;font-size:1rem;font-weight:850}
  .arp-sectionSub{margin:0 0 14px;color:var(--pu-muted);font-size:.82rem}
  .arp-row{display:grid;gap:16px}
  .arp-row--setup{grid-template-columns:minmax(300px,.85fr) minmax(0,1.15fr)}
  .arp-row--header{grid-template-columns:minmax(160px,.72fr) minmax(210px,1fr) minmax(210px,.95fr) minmax(160px,.72fr) minmax(190px,.86fr)}
  .arp-setupFields{display:grid;gap:14px;align-content:start}
  .arp-field input,.arp-field select{width:100%}
  .arp-modal .form-label{display:block;margin-bottom:6px;color:var(--pu-muted);font-size:.72rem;text-transform:uppercase;font-weight:700;letter-spacing:.05em}
  .arp-modal .form-control,.arp-modal .form-select{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-surface)!important;color:var(--pu-text)!important;padding:10px 12px;min-height:42px}
  .arp-modal .form-control::placeholder{color:var(--pu-muted)}
  .arp-modal .form-select option{background:var(--pu-surface);color:var(--pu-text)}
  .arp-readonly{background:var(--pu-soft)!important;border-color:var(--pu-border)!important}
  .arp-btn{border-radius:12px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);padding:9px 13px;font-size:.86rem;font-weight:750;cursor:pointer;transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
  .arp-btn:hover{transform:translateY(-1px);background:var(--pu-soft);box-shadow:none}
  .arp-btn:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none}
  .arp-btn--primary{border-color:var(--pu-primary);background:var(--pu-primary);color:#ffffff}
  .arp-btn--danger{border-color:var(--pu-danger);color:var(--pu-danger);background:var(--pu-danger-bg)}
  .arp-btn--cyan{border-color:var(--pu-border);color:var(--pu-text);background:var(--pu-soft)}
  .arp-btn--soft{border-color:var(--pu-border);background:var(--pu-soft)}
  .arp-pickerTrigger{width:100%;display:flex;align-items:center;justify-content:space-between;gap:12px;text-align:left}
  .arp-pickerTrigger span:first-child{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .arp-pickerTrigger span:last-child{flex-shrink:0;font-size:.72rem;letter-spacing:.04em;text-transform:uppercase;color:var(--pu-text)}
  .arp-previewCard,.arp-infoCard,.arp-statCard,.arp-lineMeta{border-radius:16px;background:var(--pu-soft);border:1px solid var(--pu-border)}
  .arp-previewCard{padding:16px}
  .arp-previewIntro strong{display:block;font-size:.98rem}
  .arp-previewIntro span{display:block;margin-top:6px;color:var(--pu-text);font-size:.84rem;line-height:1.45}
  .arp-statRow{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-top:14px}
  .arp-statCard{padding:12px}
  .arp-statLabel,.arp-infoLabel,.arp-lineMetaLabel{display:block;color:var(--pu-muted);font-size:.66rem;text-transform:uppercase;font-weight:700;letter-spacing:.06em}
  .arp-statValue,.arp-infoValue,.arp-lineMetaValue{display:block;margin-top:4px;font-size:.92rem;font-weight:800;color:var(--pu-muted)}
  .arp-previewList{display:grid;gap:10px;margin-top:14px}
  .arp-previewItem{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;border-radius:14px;padding:11px 12px;background:var(--pu-soft);border:1px solid var(--pu-border)}
  .arp-previewItemName{display:block;font-weight:800}
  .arp-previewItemMeta{display:block;margin-top:4px;color:var(--pu-muted);font-size:.78rem}
  .arp-previewItemQty{flex-shrink:0;color:var(--pu-text);font-weight:800}
  .arp-linesTop,.arp-manualHead{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
  .arp-linesTop{margin-bottom:12px}
  .arp-linesTop h6,.arp-manualHead h6{margin:0;font-size:1rem;font-weight:850}
  .arp-linesTop p,.arp-manualHead p{margin:6px 0 0;color:var(--pu-text);font-size:.8rem}
  .arp-inlineActions,.arp-actions,.arp-manualActions,.arp-pickerActions,.arp-lineActions{display:flex;gap:10px;flex-wrap:wrap}
  .arp-inlineActions,.arp-actions,.arp-manualActions,.arp-pickerActions,.arp-lineActions{justify-content:flex-end}
  .arp-manualCard{border:1px solid var(--pu-border);border-radius:20px;padding:16px;background:var(--pu-soft);margin-bottom:14px}
  .arp-manualGrid{display:grid;grid-template-columns:minmax(0,1.35fr) minmax(120px,.45fr) minmax(240px,.8fr);gap:14px}
  .arp-infoCard{padding:12px}
  .arp-tableShell{border:1px solid var(--pu-border);border-radius:22px;padding:14px 16px 16px;background:var(--pu-soft);box-shadow:none}
  .arp-tableWrap{overflow:hidden;border:1px solid var(--pu-border);border-radius:18px;background:var(--pu-soft)}
  .arp-table{width:100%;border-collapse:separate;border-spacing:0;table-layout:fixed;background:transparent!important;border:none!important}
  .arp-table th,.arp-table td{padding:12px 14px;border-bottom:1px solid var(--pu-border);vertical-align:middle}
  .arp-table th{position:sticky;top:0;z-index:2;background:var(--pu-primary);color:#ffffff;font-size:.73rem;text-transform:uppercase;letter-spacing:.06em;font-weight:800}
  .arp-table th:first-child{border-top-left-radius:14px}
  .arp-table th:last-child{border-top-right-radius:14px}
  .arp-table tbody tr{background:var(--pu-surface)}
  .arp-table tbody tr:nth-child(even){background:var(--pu-soft)}
  .arp-table tbody tr:last-child td{border-bottom:none}
  .arp-tableCell--muted{color:var(--pu-muted)}
  .arp-tableItem{display:flex;flex-direction:column;gap:5px;min-width:0}
  .arp-tableItemTop{display:flex;align-items:center;gap:8px;min-width:0}
  .arp-itemName{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:850}
  .arp-itemMeta{color:var(--pu-muted);font-size:.78rem;line-height:1.35}
  .arp-miniBadge{display:inline-flex;align-items:center;padding:4px 8px;border-radius:999px;border:1px solid var(--pu-border);background:var(--pu-soft);color:var(--pu-text);font-size:.64rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase}
  .arp-qtyCell{text-align:center;font-weight:800}
  .arp-qtyCell--pending{color:var(--pu-text)}
  .arp-qtyCell--done{color:var(--pu-text)}
  .arp-tableInput{width:100%!important;min-width:0;text-align:center}
  .arp-emptyState{border:1px dashed var(--pu-border);border-radius:20px;padding:22px 20px;background:var(--pu-soft)}
  .arp-emptyState strong{display:block;font-size:1rem}
  .arp-emptyState span{display:block;margin-top:6px;color:var(--pu-text);font-size:.86rem;line-height:1.45}
  .arp-mobileLines{display:grid;gap:12px}
  .arp-lineCard{border:1px solid var(--pu-border);border-radius:18px;padding:14px;background:var(--pu-soft)}
  .arp-lineCardTop{display:flex;justify-content:space-between;gap:10px;align-items:flex-start}
  .arp-lineCardTitle{font-size:.96rem;font-weight:850;color:var(--pu-text);line-height:1.2}
  .arp-lineCardSub{margin-top:4px;color:var(--pu-muted);font-size:.78rem}
  .arp-lineCardMeta{margin-top:10px;color:var(--pu-muted);font-size:.82rem}
  .arp-lineGrid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin-top:12px}
  .arp-alert{margin-top:12px;border:1px solid var(--pu-border);background:var(--pu-danger-bg);color:var(--pu-danger);border-radius:12px;padding:10px 12px;font-size:.84rem}
  .arp-pickerWrap{position:fixed;inset:0;z-index:10040;background:var(--pu-overlay);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:18px}
  .arp-pickerCard{width:min(640px,94vw);max-height:min(80vh,760px);overflow:auto;border-radius:24px;border:1px solid var(--pu-border);background:var(--pu-surface);box-shadow:0 20px 60px rgba(14, 32, 65, .18)}
  .arp-pickerCard--wide{width:min(760px,94vw)}
  .arp-pickerHead{display:flex;justify-content:space-between;gap:14px;align-items:flex-start;padding:18px 18px 12px;border-bottom:1px solid var(--pu-border)}
  .arp-pickerHead h6{margin:0;font-size:1rem;font-weight:850}
  .arp-pickerHead p{margin:6px 0 0;color:var(--pu-text);font-size:.82rem}
  .arp-pickerBody{padding:14px 18px 18px}
  .arp-pickerSearch{margin-bottom:12px}
  .arp-pickerList{display:grid;gap:10px}
  .arp-pickerItem{width:100%;text-align:left;border-radius:16px;border:1px solid var(--pu-border);background:var(--pu-soft);padding:12px 14px;color:var(--pu-text);transition:transform 120ms ease,border-color 120ms ease,background 120ms ease,box-shadow 120ms ease}
  .arp-pickerItem:hover{transform:translateY(-1px);border-color:var(--pu-border);background:var(--pu-soft);box-shadow:none}
  .arp-pickerItemTitle{display:block;font-size:.95rem;font-weight:800}
  .arp-pickerItemMeta{display:block;margin-top:4px;font-size:.78rem;color:var(--pu-muted)}
  .arp-pickerEmpty{border:1px dashed var(--pu-border);border-radius:16px;padding:16px;text-align:center;color:var(--pu-text);font-size:.84rem}
  .arp-confirmText{margin:0;color:var(--pu-text);font-size:.92rem;line-height:1.5}
  .arp-confirmText strong{color:var(--pu-text)}
  @media (max-width:1080px){.arp-row--setup{grid-template-columns:minmax(0,1fr)}.arp-row--header{grid-template-columns:repeat(2,minmax(0,1fr))}.arp-manualGrid{grid-template-columns:minmax(0,1fr) minmax(120px,.4fr)}.arp-manualGrid .arp-infoCard{grid-column:1 / -1}}
  @media (max-width:640px){.arp-wrap{padding:8px;align-items:flex-start}.arp-modal{width:100%;max-height:calc(100vh - 16px);border-radius:20px}.arp-head{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;padding:14px 14px 10px}.arp-headMain{min-width:0}.arp-headActions{gap:8px;justify-content:flex-end;align-self:start}.arp-title{font-size:1rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.arp-sub{margin-top:4px;font-size:.74rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.arp-badge{padding:5px 8px;font-size:.64rem}.arp-closeBtn{width:auto;min-width:76px;height:36px;padding:0 12px;border-radius:12px}.arp-body{padding:12px 14px 14px}.arp-section{padding:14px}.arp-row--header,.arp-statRow,.arp-lineGrid,.arp-manualGrid{grid-template-columns:minmax(0,1fr)}.arp-linesTop,.arp-manualHead,.arp-inlineActions,.arp-actions,.arp-manualActions,.arp-pickerActions,.arp-lineActions{flex-direction:column;align-items:stretch}.arp-inlineActions .arp-btn,.arp-actions .arp-btn,.arp-manualActions .arp-btn,.arp-pickerActions .arp-btn,.arp-lineActions .arp-btn{width:100%}.arp-pickerWrap{padding:10px;align-items:flex-start}.arp-pickerCard,.arp-pickerCard--wide{width:100%;max-height:calc(100vh - 20px);border-radius:20px}.arp-pickerHead{padding:14px 14px 10px}.arp-pickerBody{padding:12px 14px 14px}}
`;

const today = () => new Date().toISOString().slice(0, 10);

const sortByName = (items = []) =>
  [...items].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "en", {
      sensitivity: "base",
    })
  );

const sortLocations = (locations = []) =>
  [...locations].sort((left, right) => {
    const mainDelta = Number(Boolean(right?.is_main)) - Number(Boolean(left?.is_main));
    if (mainDelta !== 0) return mainDelta;

    const sortDelta = Number(left?.sort_order || 0) - Number(right?.sort_order || 0);
    if (sortDelta !== 0) return sortDelta;

    return String(left?.name || "").localeCompare(String(right?.name || ""), "en", {
      sensitivity: "base",
    });
  });

const formatQty = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  if (Number.isInteger(numericValue)) return String(numericValue);
  return numericValue.toFixed(2).replace(/\.?0+$/, "");
};

const formatMoney = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(2)} EUR` : "-";
};

const formatDateLabel = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return String(value);
  return parsedDate.toLocaleDateString("en-GB");
};

const getBusinessUnitId = () => {
  try {
    return JSON.parse(localStorage.getItem("palantir_user") || "null")?.active_business_unit_id;
  } catch (error) {
    console.warn("[AddReceivingPopup] Could not parse palantir_user from localStorage", error);
    return null;
  }
};

const labelForSupplier = (supplier) => {
  if (!supplier) return "Select supplier";
  return supplier.supplier_id ? `${supplier.supplier_id} - ${supplier.name}` : supplier.name;
};

const labelForItem = (item) => {
  if (!item) return "Select item";
  return item.code ? `${item.code} - ${item.name}` : item.name;
};

const resolveUnitName = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value.name || "";
  return String(value);
};

const resolvePackaging = (source) => {
  const unitLabel = resolveUnitName(source?.unit || source?.item?.unit);
  const subunitLabel = resolveUnitName(source?.subunit || source?.item?.subunit);
  const packSize = Number(source?.pack_size || source?.item?.pack_size || 0);

  if (packSize > 1 && subunitLabel) return `${formatQty(packSize)} x ${subunitLabel}`;
  if (subunitLabel && unitLabel && subunitLabel !== unitLabel) return `${unitLabel} / ${subunitLabel}`;
  return subunitLabel || unitLabel || "-";
};

const getPendingQty = (line) => {
  if (line?.is_manual) return 0;
  const ordered = Number(line?.quantity || 0);
  const alreadyReceived = Number(line?.quantity_received || 0);
  const cancelled = Number(line?.quantity_cancelled || 0);
  return Math.max(ordered - alreadyReceived - cancelled, 0);
};

const buildInitialState = () => ({
  suppliers: [],
  selectedSupplier: "",
  orderQuery: "",
  pendingLines: [],
  itemsList: [],
  inventoryLocations: [],
  receivingHeader: null,
  deliveryNoteNumber: "",
  receivedDate: today(),
  selectedDestinationLocationId: "",
  loadingSuppliers: false,
  loadingLocations: false,
  loadingLines: false,
  creatingHeader: false,
  saving: false,
  deletingDraft: false,
  error: "",
  showManualPanel: false,
  manualItemId: "",
  manualQty: "",
  supplierPicker: { open: false, query: "" },
  manualItemPicker: { open: false, query: "" },
  confirmState: { open: false, type: "", line: null },
  showItemPopup: false,
});

const normalizePendingLine = (line) => ({
  ...line,
  __dirty: false,
  __saveError: false,
  __receivingPersisted: Boolean(line.is_manual),
  quantity_to_receive:
    line?.quantity_to_receive !== undefined &&
    line?.quantity_to_receive !== null &&
    String(line.quantity_to_receive) !== "0"
      ? String(line.quantity_to_receive)
      : line?.is_manual && Number(line?.quantity_received || 0) > 0
        ? String(line.quantity_received)
        : "",
});

export default function AddReceivingPopup({ isOpen, onClose, onAdded }) {
  const { t, themeId } = useReceivingPopupUi();
  const isMobile = useIsMobile();
  const linesWrapRef = useRef(null);
  const pendingRequestRef = useRef(0);
  const itemsRequestRef = useRef(0);
  const sessionRef = useRef(0);
  const createRef = useRef(false);
  const [state, setState] = useState(buildInitialState);
  const [locationSelectionRequest, setLocationSelectionRequest] = useState(0);

  const {
    suppliers,
    selectedSupplier,
    orderQuery,
    pendingLines,
    itemsList,
    inventoryLocations,
    receivingHeader,
    deliveryNoteNumber,
    receivedDate,
    selectedDestinationLocationId,
    loadingSuppliers,
    loadingLocations,
    loadingLines,
    creatingHeader,
    saving,
    deletingDraft,
    error,
    showManualPanel,
    manualItemId,
    manualQty,
    supplierPicker,
    manualItemPicker,
    confirmState,
    showItemPopup,
  } = state;
  const access = useReceivingPermissions(receivingHeader);

  const setPartialState = useCallback((partialState) =>
    setState((previousState) => ({
      ...previousState,
      ...partialState,
    })), []);

  const resetState = useCallback(() => setState(buildInitialState()), []);

  const selectedSupplierData = useMemo(
    () => suppliers.find((supplier) => String(supplier.id) === String(selectedSupplier)),
    [suppliers, selectedSupplier]
  );

  const selectedManualItem = useMemo(
    () => itemsList.find((item) => String(item.id) === String(manualItemId)),
    [itemsList, manualItemId]
  );

  const filteredSuppliers = useMemo(() => {
    const query = supplierPicker.query.trim().toLowerCase();
    if (!query) return suppliers;
    return suppliers.filter((supplier) => {
      const haystack = `${supplier.supplier_id || ""} ${supplier.name || ""} ${supplier.tax_id || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [supplierPicker.query, suppliers]);

  const filteredManualItems = useMemo(() => {
    const query = manualItemPicker.query.trim().toLowerCase();
    if (!query) return itemsList;
    return itemsList.filter((item) => {
      const haystack = `${item.code || ""} ${item.name || ""} ${item.reference || ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [itemsList, manualItemPicker.query]);

  const lineSummary = useMemo(() => {
    const manualLines = pendingLines.filter((line) => line.is_manual).length;
    return { manualLines, poLines: pendingLines.length - manualLines };
  }, [pendingLines]);

  const loadSuppliers = useCallback(async () => {
    const session = sessionRef.current;
    const businessUnitId = getBusinessUnitId();
    setPartialState({ loadingSuppliers: true, error: "" });

    try {
      const response = await axios.get(
        `/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`
      );
      if (session !== sessionRef.current || businessUnitId !== getBusinessUnitId()) return;
      setPartialState({
        suppliers: sortByName(response.data || []),
        loadingSuppliers: false,
      });
    } catch (loadError) {
      if (session !== sessionRef.current || businessUnitId !== getBusinessUnitId()) return;
      console.error("❌ [AddReceivingPopup] Error fetching suppliers:", loadError);
      setPartialState({
        loadingSuppliers: false,
        error: "Could not load suppliers for this business unit.",
      });
    }
  }, [setPartialState]);

  const loadItems = useCallback(async (supplierId) => {
    const request = ++itemsRequestRef.current;
    const businessUnitId = getBusinessUnitId();
    if (!supplierId) {
      setPartialState({ itemsList: [] });
      return;
    }

    setPartialState({ itemsList: [], loadingLocations: true });
    try {
      const response = await axios.get("/api/purchasing/receivings/context");
      if (!Array.isArray(response.data?.items) || !Array.isArray(response.data?.locations)) throw new Error("Invalid receiving context");
      if (request !== itemsRequestRef.current || businessUnitId !== getBusinessUnitId()) return;
      const supplier = suppliers.find((entry) => supplierIdentityIds(entry).includes(Number(supplierId))) || supplierId;
      const visibleItems = response.data.items.filter((item) => itemMatchesSupplier(item, supplier));
      setPartialState({ itemsList: sortByName(visibleItems), inventoryLocations: sortLocations(permittedReceivingLocations(response.data.locations)), loadingLocations: false });
    } catch (loadError) {
      if (request !== itemsRequestRef.current || businessUnitId !== getBusinessUnitId()) return;
      console.error("❌ [AddReceivingPopup] Error fetching supplier items:", loadError);
      setPartialState({ itemsList: [], inventoryLocations: [], loadingLocations: false, error: "Could not load authorized receiving locations and items." });
    }
  }, [setPartialState, suppliers]);

  const loadPendingLines = useCallback(async (supplierId) => {
    const request = ++pendingRequestRef.current;
    const businessUnitId = getBusinessUnitId();
    if (!supplierId) {
      setPartialState({ pendingLines: [] });
      return;
    }

    setPartialState({ loadingLines: true, error: "" });

    try {
      const response = await axios.get(
        `/api/purchasing/receivings/pending-lines?supplier_id=${supplierId}`
      );
      if (request !== pendingRequestRef.current || businessUnitId !== getBusinessUnitId()) return null;
      if (!Array.isArray(response.data)) throw new Error("Invalid pending lines response");
      const supplier = suppliers.find((entry) => supplierIdentityIds(entry).includes(Number(supplierId))) || supplierId;
      const lines = response.data
        .filter((line) => !line.order?.supplier_id || itemSupplierMatches(line.order, supplier))
        .map(normalizePendingLine);
      setPartialState({
        pendingLines: lines,
        loadingLines: false,
      });
      return lines;
    } catch (loadError) {
      if (request !== pendingRequestRef.current || businessUnitId !== getBusinessUnitId()) return null;
      console.error("❌ [AddReceivingPopup] Error loading pending lines:", loadError);
      setPartialState({
        loadingLines: false,
        error: "Could not load pending purchase order lines for this supplier.",
      });
      return null;
    }
  }, [setPartialState, suppliers]);

  useEffect(() => {
    if (!isOpen || !access.create) return;
    const sessionCounter = sessionRef;
    const pendingCounter = pendingRequestRef;
    const itemsCounter = itemsRequestRef;
    const session = ++sessionCounter.current;
    resetState();
    loadSuppliers();

    const loadLocations = async () => {
      setPartialState({ loadingLocations: true });

      try {
        const response = await axios.get("/api/purchasing/receivings/context");
        if (!Array.isArray(response.data?.locations) || !Array.isArray(response.data?.items)) throw new Error("Invalid receiving context");
        if (session !== sessionCounter.current) return;
        setPartialState({
          inventoryLocations: sortLocations(permittedReceivingLocations(response.data.locations)),
          loadingLocations: false,
        });
      } catch (loadError) {
        if (session !== sessionCounter.current) return;
        console.warn("[AddReceivingPopup] Could not load inventory locations:", loadError);
        setPartialState({
          inventoryLocations: [],
          loadingLocations: false,
          error: "Could not load authorized receiving locations and items.",
        });
      }
    };

    loadLocations();
    return () => { sessionCounter.current++; pendingCounter.current++; itemsCounter.current++; };
  }, [isOpen, access.create, loadSuppliers, resetState, setPartialState]);

  useEffect(() => {
    if (!isOpen || !access.create) return;
    if (!selectedSupplier) {
      pendingRequestRef.current++;
      itemsRequestRef.current++;
      setPartialState({ pendingLines: [], itemsList: [] });
      return;
    }

    loadPendingLines(selectedSupplier);
    loadItems(selectedSupplier);
  }, [isOpen, access.create, selectedSupplier, loadPendingLines, loadItems, setPartialState]);

  const closeSupplierPicker = () => {
    setPartialState({ supplierPicker: { open: false, query: "" } });
  };

  const openSupplierPicker = () => {
    if (receivingHeader) return;
    setPartialState({ supplierPicker: { open: true, query: "" } });
  };

  const selectSupplier = (supplierId) => {
    if (creatingHeader || String(supplierId) === String(selectedSupplier)) { closeSupplierPicker(); return; }
    setPartialState({
      selectedSupplier: String(supplierId),
      pendingLines: [],
      loadingLines: true,
      error: "",
      supplierPicker: { open: false, query: "" },
      showManualPanel: false,
      manualItemId: "",
      manualQty: "",
    });
  };

  const handleDestinationLocationChange = (value) => {
    setPartialState({
      selectedDestinationLocationId: value,
      error: "",
    });

    if (receivingHeader?.id) {
      saveHeaderField("destination_location_id", value || null).catch(() => {});
    }
  };

  const openManualItemPicker = () => {
    setPartialState({ manualItemPicker: { open: true, query: "" } });
  };

  const closeManualItemPicker = () => {
    setPartialState({ manualItemPicker: { open: false, query: "" } });
  };

  const selectManualItem = (itemId) => {
    setPartialState({
      manualItemId: String(itemId),
      manualItemPicker: { open: false, query: "" },
      error: "",
    });
  };

  const handleCreateReceiving = async () => {
    if (!currentReceivingPermissions(receivingHeader).create) return;
    if (createRef.current || creatingHeader || loadingLines || loadingLocations) return;
    if (!inventoryLocations.length) { setPartialState({ error: "You have no authorized locations for receiving goods." }); return; }
    if (!selectedSupplier) {
      setPartialState({ error: "Select a supplier before creating the receiving draft." });
      return;
    }

    createRef.current = true;
    const session = sessionRef.current;
    const businessUnitId = getBusinessUnitId();
    const isCurrent = () => session === sessionRef.current && businessUnitId === getBusinessUnitId();
    setPartialState({ creatingHeader: true, error: "" });

    try {
      // Refresh every pending order for the supplier; browsing filters only
      // affect the initial PO list, never the receiving's scope.
      const lines = await loadPendingLines(selectedSupplier);
      if (!isCurrent() || !lines) return;
      const response = await axios.post("/api/purchasing/receivings/auto-create", {
        supplier_id: selectedSupplier,
        ...(!isActivePurchaseSupplier(selectedSupplierData)
          ? { source_po_line_ids: lines.filter((line) => !line.is_manual).map((line) => line.id) } : {}),
      });
      if (!isCurrent()) return;

      setPartialState({
        receivingHeader: { ...response.data, created_by: readStoredUser()?.id, status: "draft" },
        deliveryNoteNumber: response.data?.delivery_note_number || "",
        receivedDate: response.data?.received_date || today(),
        selectedDestinationLocationId:
          response.data?.destination_location_id || selectedDestinationLocationId || "",
        creatingHeader: false,
      });

    } catch (createError) {
      if (!isCurrent()) return;
      console.error("❌ [AddReceivingPopup] Error creating receiving draft:", createError);
      setPartialState({
        creatingHeader: false,
        error: receivingErrorMessage(createError, "Could not create the receiving draft. Please try again."),
      });
    } finally {
      createRef.current = false;
      if (isCurrent()) setPartialState({ creatingHeader: false });
    }
  };

  const saveHeaderField = (field, value) => enqueueDestinationSave("header", async () => {
    if (!receivingHeader?.id || !currentReceivingPermissions(receivingHeader).update) return;

    try {
      await axios.patch(`/api/purchasing/receivings/${receivingHeader.id}`, {
        [field]: value,
      });
      setState((previous) => ({ ...previous,
        receivingHeader: { ...previous.receivingHeader, [field]: value },
        error: "",
      }));
    } catch (saveError) {
      console.error(`❌ [AddReceivingPopup] Error saving ${field}:`, saveError);
      setPartialState({
        error: receivingErrorMessage(saveError, field === "delivery_note_number"
            ? "Could not save the delivery note number."
            : field === "destination_location_id"
            ? "Could not save the destination location."
            : "Could not save the receiving date."),
      });
      throw saveError;
    }
  });

  const handleDismiss = () => {
    onClose?.();
  };

  const resetAndClose = () => {
    resetState();
    onClose?.();
  };

  const handleSave = async () => {
    if (!currentReceivingPermissions(receivingHeader).update) return;
    if (!selectedSupplier) {
      setPartialState({ error: "Select a supplier before saving the receiving." });
      return;
    }

    if (access.verify && !deliveryNoteNumber.trim()) {
      setPartialState({ error: "Delivery note number is required before saving the receiving." });
      return;
    }

    if (pendingLines.some((line) => !receivingLineLocation(line, selectedDestinationLocationId, itemsList, inventoryLocations))) {
      setLocationSelectionRequest((request) => request + 1);
      return;
    }

    setPartialState({ saving: true, error: "" });

    try {
      await enqueueDestinationSave.flush();
      for (const line of pendingLines.filter((entry) => entry.__dirty)) {
        await persistLineQuantity(line.id, line.quantity_to_receive);
      }
      if (receivingHeader?.id) {
        await axios.patch(`/api/purchasing/receivings/${receivingHeader.id}`, {
          delivery_note_number: deliveryNoteNumber.trim(),
          received_date: receivedDate || today(),
          destination_location_id: selectedDestinationLocationId || null,
        });
      }

      if (currentReceivingPermissions(receivingHeader).verify) {
        await axios.post("/api/purchasing/receivings", {
          receiving_id: receivingHeader.id,
          supplier_id: selectedSupplier,
          destination_location_id: selectedDestinationLocationId || null,
        });
      }

      window.dispatchEvent(new CustomEvent("refreshReceivingsList"));
      onAdded?.();
      setPartialState({ saving: false });
      resetAndClose();
    } catch (saveError) {
      console.error("❌ [AddReceivingPopup] Error saving receiving:", saveError);
      setPartialState({
        saving: false,
        error: receivingErrorMessage(saveError, "Could not save the receiving. Please review the data and try again."),
      });
    }
  };

  const openConfirm = (type, line = null) => {
    setPartialState({ confirmState: { open: true, type, line } });
  };

  const closeConfirm = () => {
    if (deletingDraft) return;
    setPartialState({ confirmState: { open: false, type: "", line: null } });
  };

  const handleDeleteDraft = async () => {
    if (!currentReceivingPermissions(receivingHeader).delete) return;
    if (!receivingHeader?.id) {
      resetAndClose();
      return;
    }

    setPartialState({ deletingDraft: true, error: "" });

    try {
      await axios.delete(`/api/purchasing/receivings/${receivingHeader.id}`);
      window.dispatchEvent(new CustomEvent("refreshReceivingsList"));
      setPartialState({
        deletingDraft: false,
        confirmState: { open: false, type: "", line: null },
      });
      resetAndClose();
    } catch (deleteError) {
      console.error("❌ [AddReceivingPopup] Error deleting draft receiving:", deleteError);
      setPartialState({
        deletingDraft: false,
        error: receivingErrorMessage(deleteError, "Could not delete this receiving draft."),
      });
    }
  };

  const handleConfirmAction = async () => {
    const targetLine = confirmState.line;

    if (confirmState.type === "delete-draft") {
      await handleDeleteDraft();
      return;
    }

    if (!receivingHeader?.id || !targetLine) {
      closeConfirm();
      return;
    }

    try {
      if (confirmState.type === "remove-manual") {
        if (!currentReceivingPermissions(receivingHeader).delete) return;
        await enqueueDestinationSave(targetLine.id, () => axios.delete(
          `/api/purchasing/receivings/${receivingHeader.id}/manual-line/${targetLine.id}`
        ));
      }

      if (confirmState.type === "cancel-po") {
        const currentAccess = currentReceivingPermissions(receivingHeader);
        if (!currentAccess.update || !currentAccess.cancelOrderLine) return;
        await axios.patch(
          `/api/purchasing/receivings/${receivingHeader.id}/line/${targetLine.id}/cancel`
        );
      }

      setPartialState({
        pendingLines: pendingLines.filter((line) => line.id !== targetLine.id),
        confirmState: { open: false, type: "", line: null },
        error: "",
      });
    } catch (actionError) {
      console.error("❌ [AddReceivingPopup] Error executing line action:", actionError);
      setPartialState({
        error: receivingErrorMessage(actionError, confirmState.type === "remove-manual"
            ? "Could not remove the manual line."
            : "Could not cancel the remaining quantity for this PO line."),
      });
    }
  };

  const handleLineInputChange = (lineId, value) => {
    setPartialState({
      pendingLines: pendingLines.map((line) =>
        line.id === lineId ? { ...line, quantity_to_receive: value, __dirty: true } : line
      ),
    });
  };

  const enqueueDestinationSave = useDestinationSaveQueue();
  const persistLineQuantity = (lineId, rawValue, overrideLine) => enqueueDestinationSave(lineId, () => saveLineQuantity(lineId, rawValue, overrideLine));
  const saveLineQuantity = async (lineId, rawValue, overrideLine) => {
    if (!currentReceivingPermissions(receivingHeader).update) return;
    const targetLine = overrideLine || pendingLines.find((line) => line.id === lineId);
    if (!targetLine || !receivingHeader?.id) {
      throw new Error("Create the receiving draft before editing receiving quantities.");
    }

    const normalizedValue = String(rawValue ?? "").trim();
    const numericValue = normalizedValue === "" ? 0 : Number.parseFloat(normalizedValue);

    if (!Number.isFinite(numericValue) || numericValue < 0) {
      setPartialState({ error: "Received quantity must be a valid number equal to or above zero." });
      throw new Error("Received quantity must be a valid number equal to or above zero.");
    }

    try {
      const destinationLocationId = receivingLineLocation(targetLine, selectedDestinationLocationId, itemsList, inventoryLocations);
      if (!inventoryLocations.some((location) => Number(location.id) === Number(destinationLocationId))) {
        throw new Error("Select an authorized receiving location before saving quantities.");
      }

      if (targetLine.is_manual) {
        await axios.patch(
          `/api/purchasing/receivings/${receivingHeader.id}/manual-line/${targetLine.id}`,
          {
            item_id: targetLine.item_id || targetLine.manual_item_id || targetLine.item?.id,
            quantity_received: numericValue,
            destination_location_id: destinationLocationId,
            ...(Object.prototype.hasOwnProperty.call(targetLine, "expiration_date") ? { expiration_date: expirationDatePayload(targetLine, itemsList.find((item) => Number(item.id) === Number(targetLine.item_id || targetLine.manual_item_id || targetLine.item?.id)) || targetLine.item) } : {}),
          }
        );
      } else {
        await axios.patch(
          `/api/purchasing/receivings/${receivingHeader.id}/line/${targetLine.id}`,
          {
            quantity_received: numericValue,
            destination_location_id: destinationLocationId,
            ...(Object.prototype.hasOwnProperty.call(targetLine, "expiration_date") ? { expiration_date: expirationDatePayload(targetLine, itemsList.find((item) => Number(item.id) === Number(targetLine.item_id || targetLine.manual_item_id || targetLine.item?.id)) || targetLine.item) } : {}),
          }
        );
      }

      setState((previous) => ({
        ...previous,
        pendingLines: previous.pendingLines.map((line) => {
          if (line.id !== lineId) return line;
          const unchanged = (line.expiration_date || null) === (targetLine.expiration_date || null) && Number(line.quantity_to_receive || 0) === numericValue && Number(receivingLineLocation(line, selectedDestinationLocationId, itemsList, inventoryLocations)) === Number(destinationLocationId);
          return { ...line,
            __dirty: !unchanged,
            __saveError: unchanged ? false : line.__saveError,
            quantity_to_receive: unchanged ? (numericValue === 0 ? "" : String(numericValue)) : line.quantity_to_receive,
            ...(line.is_manual ? { quantity_received: numericValue } : {}),
          };
        }),
        error: "",
      }));
    } catch (persistError) {
      console.error("❌ [AddReceivingPopup] Error saving line quantity:", persistError);
      setState((previous) => ({ ...previous,
        pendingLines: previous.pendingLines.map((line) => line.id === lineId ? { ...line, __dirty: true, __saveError: true } : line),
        error: receivingErrorMessage(persistError, "Could not save the received quantity for this line."),
      }));
      throw persistError;
    }
  };

  const changeLineDestination = (index, destination, source) => {
    const line = pendingLines[index];
    if (!line) return;
    const nextLine = { ...line, destination_location_id: destination, destination_location_source: source, __dirty: Boolean(line.quantity_to_receive) || line.__dirty };
    setState((previous) => ({ ...previous, pendingLines: previous.pendingLines.map((entry, i) => i === index ? nextLine : entry) }));
    if (destination && receivingHeader?.id && Number(line.quantity_to_receive) > 0) persistLineQuantity(line.id, line.quantity_to_receive, nextLine).catch(() => {});
  };

  useAuthorizedReceivingDestinations({
    lines: pendingLines,
    items: itemsList,
    locations: inventoryLocations,
    headerLocationId: selectedDestinationLocationId,
    onChange: changeLineDestination,
    disabled: !isOpen || !receivingHeader?.id || !access.update || saving || loadingLocations,
    scopeKey: receivingHeader?.id,
  });

  const handleAddManualLine = async () => {
    if (!currentReceivingPermissions(receivingHeader).update) return;
    if (!isActivePurchaseSupplier(selectedSupplierData)) {
      setPartialState({ error: "This supplier is inactive. Select an active supplier for new purchases." });
      return;
    }
    if (!receivingHeader?.id) {
      setPartialState({ error: "Create the receiving draft before adding manual items." });
      return;
    }

    const numericQty = Number.parseFloat(manualQty);
    if (!manualItemId || !Number.isFinite(numericQty) || numericQty <= 0) {
      setPartialState({
        error: "Select an item and enter a valid quantity before adding the manual line.",
      });
      return;
    }

    const destinationLocationId = receivingLineLocation({ item_id: manualItemId }, selectedDestinationLocationId, itemsList, inventoryLocations);
    if (!inventoryLocations.some((location) => Number(location.id) === Number(destinationLocationId))) {
      setLocationSelectionRequest((request) => request + 1);
      setPartialState({ error: "Select an authorized receiving location before saving quantities." }); return;
    }
    try {
      const response = await axios.post(
        `/api/purchasing/receivings/${receivingHeader.id}/manual-line`,
        {
          item_id: manualItemId,
          quantity_received: numericQty,
          destination_location_id: destinationLocationId,
        }
      );

      const resolvedPrice = response.data?.unit_price_received ?? null;

      const newLine = normalizePendingLine({
        id: response.data?.id || Date.now(),
        item_id: Number(manualItemId),
        manual_item_id: Number(manualItemId),
        destination_location_id: response.data?.destination_location_id || null,
        quantity: 0,
        quantity_received: numericQty,
        is_manual: true,
        item: selectedManualItem || { id: manualItemId, name: "Manual item" },
        order: null,
        unit_price_received: resolvedPrice,
        unit_price: resolvedPrice,
        pack_size: selectedManualItem?.pack_size || 1,
        unit:
          typeof selectedManualItem?.unit === "object"
            ? selectedManualItem.unit
            : { name: resolveUnitName(selectedManualItem?.unit) || "-" },
        subunit:
          typeof selectedManualItem?.subunit === "object"
            ? selectedManualItem.subunit
            : { name: resolveUnitName(selectedManualItem?.subunit) || "-" },
        quantity_to_receive: String(numericQty),
      });

      setPartialState({
        pendingLines: [newLine, ...pendingLines],
        manualItemId: "",
        manualQty: "",
        showManualPanel: false,
        error: "",
      });

      linesWrapRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } catch (manualError) {
      console.error("❌ [AddReceivingPopup] Error adding manual item:", manualError);
      setPartialState({ error: receivingErrorMessage(manualError, "Could not add this manual receiving line.") });
    }
  };

  const handleItemAdded = async (newItem) => {
    try {
      const refreshedItems = await axios.get("/api/purchasing/receivings/context");
      const enrichedItem = refreshedItems.data?.items?.find((item) => Number(item.id) === Number(newItem.id));
      if (!enrichedItem) throw new Error("The item is not available for receiving.");
      setPartialState({
        itemsList: [enrichedItem, ...itemsList.filter((item) => item.id !== enrichedItem.id)],
        manualItemId: String(enrichedItem.id),
        showItemPopup: false,
        showManualPanel: true,
        error: "",
      });
    } catch (itemError) {
      console.error("❌ [AddReceivingPopup] Error refreshing newly created item:", itemError);
      setPartialState({
        showItemPopup: false,
        error: "The new item was created, but its full data could not be reloaded.",
      });
    }
  };

  const confirmCopy = useMemo(() => {
    if (confirmState.type === "delete-draft") {
      return {
        title: "Delete Draft Receiving",
        subtitle: "This confirmation now follows the Pilcore modal flow.",
        message: t("Are you sure you want to delete {name}? This action cannot be undone.", { name: receivingHeader?.receiving_code || t("this receiving draft") }),
        keepLabel: "Keep Draft",
        actionLabel: deletingDraft ? "Deleting..." : "Delete Draft",
      };
    }

    if (confirmState.type === "remove-manual") {
      return {
        title: "Remove Manual Line",
        subtitle: "This line is not linked to any purchase order.",
        message: t("Remove {name} from the receiving draft?", { name: confirmState.line?.item?.name || t("this manual line") }),
        keepLabel: "Keep Line",
        actionLabel: "Remove Line",
      };
    }

    return {
      title: "Cancel Pending Quantity",
      subtitle: "This affects the remaining PO quantity for the selected line.",
      message: t("Cancel the pending quantity for {name}?", { name: confirmState.line?.item?.name || t("this PO line") }),
      keepLabel: "Keep Line",
      actionLabel: "Cancel Pending",
    };
  }, [confirmState.line, confirmState.type, deletingDraft, receivingHeader?.receiving_code, t]);

  const renderSetupSection = () => (
    <div className="arp-setup">
      <div className="arp-setupContext">
        <div className="arp-field">
          <label className="form-label">{t("Supplier *")}</label>
          <button type="button" className="arp-btn arp-pickerTrigger" onClick={openSupplierPicker}
            disabled={loadingSuppliers || creatingHeader} aria-haspopup="dialog" aria-expanded={supplierPicker.open}>
            <span>{loadingSuppliers ? t("Loading suppliers...") : t(labelForSupplier(selectedSupplierData))}</span><span>{t("Choose")}</span>
          </button>
        </div>
        <div className="arp-field">
          <label className="form-label" htmlFor="receiving-order-search">{t("Search pending orders")}</label>
          <div className="arp-poSearch arp-setupSearch"><FiSearch aria-hidden="true" />
            <input id="receiving-order-search" type="search" value={orderQuery}
              onChange={(event) => setPartialState({ orderQuery: event.target.value })}
              placeholder={t("Search by PO, supplier, item or code...")} disabled={creatingHeader} />
          </div>
        </div>
      </div>
      {!loadingLocations && !inventoryLocations.length && <p className="arp-alert" role="alert">{t("You have no authorized locations for receiving goods.")}</p>}
      <PendingReceivingOrders businessUnitId={getBusinessUnitId()} selectedSupplierId={selectedSupplier} supplierCatalog={suppliers}
        query={orderQuery} onQueryChange={(value) => setPartialState({ orderQuery: value })} disabled={creatingHeader} />
      {error && <div className="arp-alert" role="alert">{t(error)}</div>}
      <footer className="arp-setupFooter">
        <span>{t("All pending orders for the supplier will be available. You can also receive items without a PO.")}</span>
        <button type="button" className="arp-btn arp-btn--primary" onClick={handleCreateReceiving}
          disabled={!selectedSupplier || creatingHeader || loadingSuppliers || loadingLines || loadingLocations || !inventoryLocations.length}>
          {creatingHeader ? t("Creating...") : t("Create Receiving")}
        </button>
      </footer>
    </div>
  );

  const renderLineDestination = (line, index) => {
    const itemId = line.item_id || line.manual_item_id || line.item?.id;
    const item = itemsList.find((entry) => Number(entry.id) === Number(itemId));
    const name = line.item?.name || item?.name || line.item_name || line.description || t("Item #{id}", { id: itemId });
    return (
      <ReceivingLocationPicker
        compact
        label={`${t("Destination")}: ${name}`}
        locations={inventoryLocations}
        value={receivingLineLocation(line, selectedDestinationLocationId, itemsList, inventoryLocations)}
        disabled={saving || loadingLocations}
        onChange={(destination) => changeLineDestination(index, destination, "manual")}
      />
    );
  };

  const renderLineExpiration = (line) => (
    <ExpirationDateField line={line}
      item={itemsList.find((item) => Number(item.id) === Number(line.item_id || line.manual_item_id || line.item?.id)) || line.item}
      disabled={saving || !access.update}
      onChange={(value) => setState((previous) => ({ ...previous, pendingLines: previous.pendingLines.map((entry) =>
        entry.id === line.id ? { ...entry, expiration_date: value, __dirty: true } : entry) }))}
      onBlur={() => { if (line.__dirty && Number(line.quantity_to_receive) > 0) persistLineQuantity(line.id, line.quantity_to_receive).catch(() => {}); }} />
  );

  const renderDesktopLines = () => (
    <div className="arp-tableShell">
      <div className="arp-tableWrap" ref={linesWrapRef}>
        <table className="arp-table">
          <thead>
            <tr>
              <th style={{ width: "15%" }}>{t("Order / ETA")}</th>
              <th style={{ width: "26%" }}>{t("Item")}</th>
              <th style={{ width: "6%", textAlign: "center" }}>{t("Ordered")}</th>
              <th style={{ width: "6%", textAlign: "center" }}>{t("Received")}</th>
              <th style={{ width: "6%", textAlign: "center" }}>{t("Pending")}</th>
              <th style={{ width: "7%", textAlign: "center" }} title={t("Receiving Now")}>Q</th>
              <th style={{ width: "13%" }}>{t("Location")}</th>
              <th style={{ width: "13%" }}>{t("Expiration date")}</th>
              <th style={{ width: "8%", textAlign: "right" }}>{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {pendingLines.map((line, index) => {
              const pendingQty = getPendingQty(line);
              const currentQty = line.quantity_to_receive || "";

              return (
                <tr key={`${line.is_manual ? "manual" : "po"}-${line.id}`}>
                  <td className="arp-tableCell--muted">
                    {line.is_manual ? (
                      <>
                        <div className="arp-itemName">{t("Manual line")}</div>
                        <div className="arp-itemMeta">{t("Not linked to a purchase order")}</div>
                      </>
                    ) : (
                      <>
                        <div className="arp-itemName">{line.order?.order_code || "-"}</div>
                        <div className="arp-itemMeta"> {t("ETA")} {formatDateLabel(line.order?.expected_delivery_date)}
                        </div>
                      </>
                    )}
                  </td>

                  <td>
                    <div className="arp-tableItem">
                      <div className="arp-tableItemTop">
                        <span className="arp-itemName">
                          {line.item?.name || t("Item #{id}", { id: line.item_id })}
                        </span>
                        {line.is_manual && <span className="arp-miniBadge">{t("Manual")}</span>}
                      </div>
                      <div className="arp-itemMeta">
                        {line.item?.code ? `${line.item.code} · ` : ""}
                        {resolvePackaging(line)}
                        {line.unit_price_received
                          ? ` · ${formatMoney(line.unit_price_received)}`
                          : ""}
                      </div>
                    </div>
                  </td>

                  <td className="arp-qtyCell">{line.is_manual ? "-" : formatQty(line.quantity)}</td>
                  <td className="arp-qtyCell">{formatQty(line.quantity_received)}</td>
                  <td
                    className={`arp-qtyCell ${
                      line.is_manual || pendingQty > 0 ? "arp-qtyCell--pending" : "arp-qtyCell--done"
                    }`}
                  >
                    {line.is_manual ? "-" : formatQty(pendingQty)}
                  </td>

                  <td>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={currentQty}
                        aria-invalid={line.__saveError || undefined}
                        title={line.__saveError ? t("This quantity has not been saved.") : undefined}
                        disabled={saving || !inventoryLocations.some((location) => Number(location.id) === Number(receivingLineLocation(line, selectedDestinationLocationId, itemsList, inventoryLocations)))}
                      onChange={(event) => handleLineInputChange(line.id, event.target.value)}
                      onBlur={(event) => persistLineQuantity(line.id, event.target.value).catch(() => {})}
                      className="form-control arp-tableInput"
                      placeholder="0.00"
                    />
                    {line.__saveError && <small className="receiving-quantity-error">{t("This quantity has not been saved.")}</small>}
                  </td>

                  <td>{renderLineDestination(line, index)}</td>
                  <td>{renderLineExpiration(line)}</td>

                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="arp-btn arp-btn--danger"
                      disabled={line.is_manual ? !access.delete : !access.cancelOrderLine}
                      onClick={() => openConfirm(line.is_manual ? "remove-manual" : "cancel-po", line)}
                    >
                      {line.is_manual ? t("Remove") : t("Cancel")}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderMobileLines = () => (
    <div className="arp-mobileLines">
      {pendingLines.map((line, index) => {
        const pendingQty = getPendingQty(line);
        const currentQty = line.quantity_to_receive || "";

        return (
          <div className="arp-lineCard" key={`${line.is_manual ? "manual" : "po"}-${line.id}`}>
            <div className="arp-lineCardTop">
              <div>
                <div className="arp-lineCardTitle">{line.item?.name || t("Item #{id}", { id: line.item_id })}</div>
                <div className="arp-lineCardSub">
                  {line.is_manual
                    ? t("Manual line · Not linked to a purchase order")
                    : `${line.order?.order_code || t("PO")} · ETA ${formatDateLabel(
                        line.order?.expected_delivery_date
                      )}`}
                </div>
              </div>
              {line.is_manual && <span className="arp-miniBadge">{t("Manual")}</span>}
            </div>

            <div className="arp-lineCardMeta">
              {resolvePackaging(line)}
              {line.unit_price_received
                ? ` · ${formatMoney(line.unit_price_received)}`
                : ""}
            </div>

            <div className="arp-lineGrid">
              <div className="arp-lineMeta">
                <span className="arp-lineMetaLabel">{t("Ordered")}</span>
                <span className="arp-lineMetaValue">{line.is_manual ? "-" : formatQty(line.quantity)}</span>
              </div>
              <div className="arp-lineMeta">
                <span className="arp-lineMetaLabel">{t("Received")}</span>
                <span className="arp-lineMetaValue">{formatQty(line.quantity_received)}</span>
              </div>
              <div className="arp-lineMeta">
                <span className="arp-lineMetaLabel">{t("Pending")}</span>
                <span className="arp-lineMetaValue">{line.is_manual ? "-" : formatQty(pendingQty)}</span>
              </div>
              <div className="arp-lineMeta">
                <span className="arp-lineMetaLabel" title={t("Receiving Now")}>Q</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={currentQty}
                        aria-invalid={line.__saveError || undefined}
                        title={line.__saveError ? t("This quantity has not been saved.") : undefined}
                        disabled={saving || !inventoryLocations.some((location) => Number(location.id) === Number(receivingLineLocation(line, selectedDestinationLocationId, itemsList, inventoryLocations)))}
                  onChange={(event) => handleLineInputChange(line.id, event.target.value)}
                  onBlur={(event) => persistLineQuantity(line.id, event.target.value).catch(() => {})}
                  className="form-control arp-tableInput"
                  placeholder="0.00"
                />
                    {line.__saveError && <small className="receiving-quantity-error">{t("This quantity has not been saved.")}</small>}
              </div>
              <div className="arp-lineMeta">
                <span className="arp-lineMetaLabel">{t("Location")}</span>
                {renderLineDestination(line, index)}
              </div>
              <div className="arp-lineMeta">
                <span className="arp-lineMetaLabel">{t("Expiration date")}</span>
                {renderLineExpiration(line)}
              </div>
            </div>

            <div className="arp-lineActions">
              <button
                type="button"
                className="arp-btn arp-btn--danger"
                disabled={line.is_manual ? !access.delete : !access.cancelOrderLine}
                onClick={() => openConfirm(line.is_manual ? "remove-manual" : "cancel-po", line)}
              >
                {line.is_manual ? t("Remove Line") : t("Cancel Pending")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderReceivingSection = () => (
    <>
      <div className="arp-section">
        <h6 className="arp-sectionTitle">{t("Receiving Header")}</h6>
        <p className="arp-sectionSub"> {t("Keep the top compact. The useful work is below, so this header only keeps the essentials.")} </p>

        <div className="arp-row arp-row--header">
          <div className="arp-field">
            <label className="form-label">{t("Receiving Code")}</label>
            <input
              type="text"
              readOnly
              value={receivingHeader.receiving_code || `#${receivingHeader.id}`}
              className="form-control arp-readonly"
            />
          </div>

          <div className="arp-field">
            <label className="form-label">{t("Supplier")}</label>
            <input
              type="text"
              readOnly
              value={t(labelForSupplier(selectedSupplierData))}
              className="form-control arp-readonly"
            />
          </div>

          <div className="arp-field">
            <label className="form-label">{t("Delivery Note Number *")}</label>
            <input
              type="text"
              value={deliveryNoteNumber}
              onChange={(event) => setPartialState({ deliveryNoteNumber: event.target.value, error: "" })}
              disabled={saving}
              onBlur={(event) => saveHeaderField("delivery_note_number", event.target.value.trim()).catch(() => {})}
              className="form-control"
              placeholder={t("Supplier delivery note number...")}
            />
          </div>

          <div className="arp-field">
            <label className="form-label">{t("Received Date")}</label>
            <input
              type="date"
              value={receivedDate || today()}
              onChange={(event) => setPartialState({ receivedDate: event.target.value || today(), error: "" })}
              disabled={saving}
              onBlur={(event) => saveHeaderField("received_date", event.target.value || today()).catch(() => {})}
              className="form-control"
            />
          </div>

          <div className="arp-field">
            <label className="form-label">{t("Destination Location")}</label>
            <ReceivingLocationPicker allowInherited value={selectedDestinationLocationId} locations={inventoryLocations}
              requestOpen={locationSelectionRequest}
              label={t("Destination Location")} onChange={handleDestinationLocationChange} disabled={loadingLocations || saving} />
          </div>
        </div>
      </div>

      <div className="arp-section">
        {!loadingLocations && !inventoryLocations.length && <p className="arp-alert" role="alert">{t("You have no authorized locations for receiving goods.")}</p>}
        <div className="arp-linesTop">
          <div>
            <h6>{t("Receiving Lines")}</h6>
            <p>
              {lineSummary.poLines} {t("PO lines ready to receive")} {lineSummary.manualLines
                ? ` · ${t("{count} manual lines", { count: lineSummary.manualLines })}`
                : ""} {t(". Items outside the PO flow live in a separate add panel instead of a long ugly inline selector.")} </p>
          </div>

          <div className="arp-inlineActions">
            <button
              type="button"
              className={`arp-btn ${showManualPanel ? "arp-btn--soft" : "arp-btn--cyan"}`}
              disabled={!isActivePurchaseSupplier(selectedSupplierData)}
              onClick={() => setPartialState({ showManualPanel: !showManualPanel, error: "" })}
            >
              {showManualPanel ? t("Hide Manual Add") : t("+ Add Item Not in PO")}
            </button>
          </div>
        </div>

        {selectedSupplierData && !isActivePurchaseSupplier(selectedSupplierData) && <p className="arp-alert" role="alert">
          {t("This supplier is inactive. You can receive existing purchase orders, but cannot add items outside a purchase order.")}
        </p>}

        {showManualPanel && (
          <div className="arp-manualCard">
            <div className="arp-manualHead">
              <div>
                <h6>{t("Manual Receiving Line")}</h6>
                <p> {t("Use this only when the delivery contains something not linked to the pending purchase order lines above.")} </p>
              </div>
            </div>

            <div className="arp-manualGrid">
              <div className="arp-field">
                <label className="form-label">{t("Item *")}</label>
                {isMobile ? (
                  <select
                    value={manualItemId}
                    onChange={(event) => selectManualItem(event.target.value)}
                    className="form-select"
                  >
                    <option value="">{t("Select item")}</option>
                    {itemsList.map((item) => (
                      <option key={item.id} value={item.id}>
                        {t(labelForItem(item))}
                      </option>
                    ))}
                  </select>
                ) : (
                  <button
                    type="button"
                    className="arp-btn arp-pickerTrigger"
                    onClick={openManualItemPicker}
                  >
                    <span>{t(labelForItem(selectedManualItem))}</span>
                    <span>{manualItemPicker.open ? t("Close") : t("Choose")}</span>
                  </button>
                )}
              </div>

              <div className="arp-field">
                <label className="form-label">{t("Qty *")}</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={manualQty}
                  onChange={(event) => setPartialState({ manualQty: event.target.value, error: "" })}
                  className="form-control"
                  placeholder="0.00"
                />
              </div>

              <div className="arp-infoCard">
                <span className="arp-infoLabel">{t("Packaging")}</span>
                <span className="arp-infoValue">
                  {selectedManualItem ? resolvePackaging(selectedManualItem) : "—"}
                </span>
              </div>
            </div>

            <div className="arp-manualActions">
              <button
                type="button"
                className="arp-btn"
                disabled={!access.createItem} onClick={() => { if (currentReceivingPermissions(receivingHeader).createItem) setPartialState({ showItemPopup: true }); }}
              > {t("+ New Item")} </button>
              <button
                type="button"
                className="arp-btn"
                onClick={() =>
                  setPartialState({
                    showManualPanel: false,
                    manualItemId: "",
                    manualQty: "",
                    error: "",
                  })
                }
              > {t("Cancel")} </button>
              <button type="button" className="arp-btn arp-btn--primary" onClick={handleAddManualLine}> {t("Add Line")} </button>
            </div>
          </div>
        )}

        {loadingLines ? (
          <div className="arp-emptyState">
            <strong>{t("Loading receiving lines...")}</strong>
            <span>{t("Fetching the pending purchase order lines for this supplier.")}</span>
          </div>
        ) : pendingLines.length ? (
          isMobile ? renderMobileLines() : renderDesktopLines()
        ) : (
          <div className="arp-emptyState">
            <strong>{t("No pending lines yet.")}</strong>
            <span> {t("This supplier has no open PO lines pending receipt. You can still add a manual receiving line with the button above if the delivery arrived outside the normal PO flow.")} </span>
          </div>
        )}

        {error && <div className="arp-alert">{t(error)}</div>}

        <div className="arp-actions">
          {access.delete && <button
            type="button"
            className="arp-btn arp-btn--danger"
            onClick={() => openConfirm("delete-draft")}
            disabled={saving || deletingDraft}
          > {t("Delete Draft")} </button>}

          <button
            type="button"
            className="arp-btn arp-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t("Saving...") : access.verify ? t("Save Receiving") : t("Save Draft")}
          </button>
        </div>
      </div>
    </>
  );

  if (!isOpen || !access.create) return null;

  return (
    <>
      <style>{numberInputStyle}</style>
      <style>{popupCss}</style>

      <div
        className="arp-wrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) handleDismiss();
        }}
      >
        <div className={`arp-modal${receivingHeader ? "" : " arp-modal--setup"}`} role="dialog" aria-modal="true" aria-label={t("New Purchase Receiving")} onMouseDown={(event) => event.stopPropagation()}>
          <div className="arp-head">
            <div className="arp-headMain">
              <h5 className="arp-title">{isMobile ? t("New Receiving") : t("New Purchase Receiving")}</h5>
              <div className="arp-sub">
                {receivingHeader
                  ? `${t(labelForSupplier(selectedSupplierData))}${deliveryNoteNumber ? ` · DN ${deliveryNoteNumber}` : ""}`
                  : t("Browse pending purchase orders and choose the supplier you are receiving from.")}
              </div>
            </div>

            <div className="arp-headActions">
              <span className="arp-badge">{receivingPopupStatus(t, receivingHeader?.status)}</span>
              <button
                type="button"
                className="arp-closeBtn"
                aria-label={t("Close receiving popup")}
                title={t("Close")}
                onClick={handleDismiss}
              >
                {isMobile ? t("Close") : t("X")}
              </button>
            </div>
          </div>

          <div className="arp-body">{receivingHeader ? renderReceivingSection() : renderSetupSection()}</div>
        </div>

        {confirmState.open && (
          <div
            className="arp-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeConfirm();
            }}
          >
            <div className="arp-pickerCard" onMouseDown={(event) => event.stopPropagation()}>
              <div className="arp-pickerHead">
                <div>
                  <h6>{t(confirmCopy.title)}</h6>
                  <p>{t(confirmCopy.subtitle)}</p>
                </div>
                <button
                  type="button"
                  className="arp-btn"
                  onClick={closeConfirm}
                  disabled={deletingDraft}
                > {t("Close")} </button>
              </div>

              <div className="arp-pickerBody">
                <p className="arp-confirmText">
                  <strong>{confirmCopy.message}</strong>
                </p>

                <div className="arp-pickerActions">
                  <button
                    type="button"
                    className="arp-btn"
                    onClick={closeConfirm}
                    disabled={deletingDraft}
                  >
                    {t(confirmCopy.keepLabel)}
                  </button>
                  <button
                    type="button"
                    className="arp-btn arp-btn--danger"
                    onClick={handleConfirmAction}
                    disabled={deletingDraft}
                  >
                    {t(confirmCopy.actionLabel)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {supplierPicker.open && (
          <div
            className="arp-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeSupplierPicker();
            }}
          >
            <div className="arp-pickerCard" onMouseDown={(event) => event.stopPropagation()}>
              <div className="arp-pickerHead">
                <div>
                  <h6>{t("Select Supplier")}</h6>
                  <p>{t("Choose the supplier for this receiving draft with the same Pilcore picker flow.")}</p>
                </div>
                <button type="button" className="arp-btn" onClick={closeSupplierPicker}> {t("Close")} </button>
              </div>

              <div className="arp-pickerBody">
                <input
                  type="search"
                  value={supplierPicker.query}
                  onChange={(event) =>
                    setPartialState({
                      supplierPicker: {
                        ...supplierPicker,
                        query: event.target.value,
                      },
                    })
                  }
                  className="form-control arp-pickerSearch"
                  placeholder={t("Search supplier by id or name...")}
                  autoFocus
                />

                <div className="arp-pickerList">
                  {filteredSuppliers.length ? (
                    filteredSuppliers.map((supplier) => (
                      <button
                        key={supplier.id}
                        type="button"
                        className="arp-pickerItem"
                        onClick={() => selectSupplier(supplier.id)}
                      >
                        <span className="arp-pickerItemTitle">{t(labelForSupplier(supplier))}</span>
                        <span className="arp-pickerItemMeta">
                          {supplier.tax_id || t("Use this supplier for the receiving draft")}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="arp-pickerEmpty">{t("No suppliers match your search.")}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {!isMobile && manualItemPicker.open && (
          <div
            className="arp-pickerWrap pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeManualItemPicker();
            }}
          >
            <div
              className="arp-pickerCard arp-pickerCard--wide"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="arp-pickerHead">
                <div>
                  <h6>{t("Select Item")}</h6>
                  <p>{t("Pick the item with a clean modal instead of a long inline selector.")}</p>
                </div>
                <button type="button" className="arp-btn" onClick={closeManualItemPicker}> {t("Close")} </button>
              </div>

              <div className="arp-pickerBody">
                <input
                  type="search"
                  value={manualItemPicker.query}
                  onChange={(event) =>
                    setPartialState({
                      manualItemPicker: {
                        ...manualItemPicker,
                        query: event.target.value,
                      },
                    })
                  }
                  className="form-control arp-pickerSearch"
                  placeholder={t("Search by code or item name...")}
                  autoFocus
                />

                <div className="arp-pickerList">
                  {filteredManualItems.length ? (
                    filteredManualItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className="arp-pickerItem"
                        onClick={() => selectManualItem(item.id)}
                      >
                        <span className="arp-pickerItemTitle">{t(labelForItem(item))}</span>
                        <span className="arp-pickerItemMeta"> {t("Packaging:")} {resolvePackaging(item)}
                          {item.reference ? ` · Ref: ${item.reference}` : ""}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="arp-pickerEmpty">{t("No items match your search.")}</div>
                  )}
                </div>

                <div className="arp-pickerActions">
                  <button
                    type="button"
                    className="arp-btn arp-btn--cyan"
                    disabled={!access.createItem} onClick={() => { if (currentReceivingPermissions(receivingHeader).createItem) setPartialState({ showItemPopup: true }); }}
                  > {t("+ New Item")} </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showItemPopup && access.createItem && (
          <AddItemPopup
            isOpen={showItemPopup}
            context="receiving"
            supplierIdLock={selectedSupplier}
            onClose={() => setPartialState({ showItemPopup: false })}
            onAdded={handleItemAdded}
          />
        )}
      </div>
    </>
  );
}
